import { Pagination } from "antd";
import React from "react";
import CustomButton from "../../../commonComponents/button";
import { AI_RESPONSE_COPY } from "./constants";

/**
 * Version pager + Undo for a composer with AI draft history (EAI-1006).
 *
 * Feed it a `useDraftVersions` result. Renders nothing until there is something
 * to offer, so it is safe to mount unconditionally.
 *
 * The two controls do different jobs and appear at different times. The pager
 * steps between AI versions and needs at least two of them. Undo abandons the
 * whole thing and hands back what the user wrote before any refining — that is
 * worth offering from the very first refine, when there is only one AI version
 * and no pager. "I refined my own wording and don't like it" is the common case,
 * and it wants one button, not repeated back-stepping.
 *
 * It is deliberately a sibling of the composer rather than an overlay on it —
 * floating these over a textarea covers the message being written.
 *
 * @param {{
 *   versions: {
 *     versions: Array<{ content: string, subject?: string, historyId: number|null }>,
 *     index: number,
 *     goTo: Function,
 *     undo: Function,
 *     canUndo: boolean,
 *     hasHistory: boolean,
 *   },
 *   className?: string,
 * }} props
 */
export default function DraftVersionControls({ versions, className = "" }) {
  // Undo appears after the first refine; the pager only once there are two AI
  // versions to move between. So the two are gated separately — returning null
  // on `hasHistory` alone would hide Undo exactly when it is most wanted.
  if (!versions?.canUndo && !versions?.hasHistory) return null;

  return (
    <div className={`flex items-center ${className}`}>
      {versions.hasHistory && (
        <Pagination
          simple={{ readOnly: true }}
          current={versions.index + 1}
          total={versions.versions.length}
          pageSize={1}
          size="small"
          onChange={page => versions.goTo(page - 1)}
        />
      )}
      <CustomButton
        htmlType="button"
        color="default"
        variant="dashed"
        size="small"
        btnText={AI_RESPONSE_COPY.UNDO}
        tooltipTitle={AI_RESPONSE_COPY.UNDO_TOOLTIP}
        disabled={!versions.canUndo}
        handleClick={versions.undo}
      />
    </div>
  );
}
